import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { ProgramariUltimaOraService } from '../programari-ultima-ora.service';
import { MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { finalize, map, Observable, of, startWith } from 'rxjs';
import { helper } from 'src/environments/helper';
import { SlotZi, SlotGrupUpdate } from '../programari-ultima-ora.interface';
import { GhiseuModuleService } from '../../ghiseu-module/ghiseu-module.service';
import { LocatiiService } from '../../locatii/locatii.service';
import { ServiciiService } from '../../servicii/servicii.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
    selector: 'app-add-programari-ultima-ora',
    templateUrl: './add-programari-ultima-ora.component.html',
    styleUrls: ['./add-programari-ultima-ora.component.scss']
})
export class AddProgramariUltimaOraComponent implements OnInit {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;

    // Date originale
    module: any[] = [];
    locatii: any[] = [];
    servicii: any[] = [];

    // Date filtrate pentru autocomplete
    filteredModule$!: Observable<any[]>;
    filteredLocatii$!: Observable<any[]>;
    filteredServicii$!: Observable<any[]>;

    // FormControls pentru autocomplete
    modulControl = new FormControl<any>(null, [Validators.required]);
    locatieControl = new FormControl<any>(null, [Validators.required]);
    serviciuControl = new FormControl<any>(null, [Validators.required]);

    // Zile configurate
    zile: SlotZi[] = [];
    emails: string = '';

    // Pentru adăugare zi nouă
    showAddZi = false;
    newZi: SlotZi = { day_of_week: -1, time_from: '' };

    zileSaptamana: { key: string; value: number }[] = [
        { key: 'Luni', value: 1 },
        { key: 'Marți', value: 2 },
        { key: 'Miercuri', value: 3 },
        { key: 'Joi', value: 4 },
        { key: 'Vineri', value: 5 },
        { key: 'Sâmbătă', value: 6 },
        { key: 'Duminică', value: 0 },
    ];

    constructor(
        private dialogRef: MatDialogRef<AddProgramariUltimaOraComponent>,
        private programariSloturiUltimaOraService: ProgramariUltimaOraService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private ghiseuModuleService: GhiseuModuleService,
        private locatiiService: LocatiiService,
        private serviciiService: ServiciiService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.setupAutocompleteFilters();
        this.loadModule();
        this.loadLocatii();
        this.loadServicii();
    }

    setupAutocompleteFilters(): void {
        // Filter pentru module
        this.filteredModule$ = this.modulControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterModule(value))
        );

        // Filter pentru locații
        this.filteredLocatii$ = this.locatieControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterLocatii(value))
        );

        // Filter pentru servicii
        this.filteredServicii$ = this.serviciuControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterServicii(value))
        );
    }

    private _filterModule(value: any): any[] {
        if (!value || typeof value !== 'string') {
            return this.module;
        }
        const filterValue = this.normalizeString(value);
        return this.module.filter(m => this.normalizeString(m.name || '').includes(filterValue));
    }

    private _filterLocatii(value: any): any[] {
        if (!value || typeof value !== 'string') {
            return this.locatii;
        }
        const filterValue = this.normalizeString(value);
        return this.locatii.filter(l => this.normalizeString(l.name || '').includes(filterValue));
    }

    private _filterServicii(value: any): any[] {
        if (!value || typeof value !== 'string') {
            return this.servicii;
        }
        const filterValue = this.normalizeString(value);
        return this.servicii.filter(s => this.normalizeString(s.web_name || '').includes(filterValue));
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    // Display functions pentru autocomplete
    displayModul = (modul: any): string => {
        return modul?.name || '';
    }

    displayLocatie = (locatie: any): string => {
        return locatie?.name || '';
    }

    displayServiciu = (serviciu: any): string => {
        return serviciu?.web_name || '';
    }

    // Deschide panoul autocomplete la focus
    onFocusModul(trigger: MatAutocompleteTrigger): void {
        if (!this.modulControl.value) {
            this.modulControl.setValue('');
        }
        trigger.openPanel();
    }

    onFocusLocatie(trigger: MatAutocompleteTrigger): void {
        if (!this.locatieControl.value) {
            this.locatieControl.setValue('');
        }
        trigger.openPanel();
    }

    onFocusServiciu(trigger: MatAutocompleteTrigger): void {
        if (!this.serviciuControl.value) {
            this.serviciuControl.setValue('');
        }
        trigger.openPanel();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.programariSloturiUltimaOraService.setToken(token);
        });
    }

    loadModule() {
        this.isLoading = true;
        this.ghiseuModuleService.getModule().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.module = res;
            },
            error: (err) => {
                console.error('Error in get ghiseu module: ', err);
            }
        })
    }

    loadLocatii() {
        this.isLoading = true;
        this.locatiiService.getLocatii().pipe(
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.locatii = res;
            },
            error: (err) => {
                console.error('Error in get locatii: ', err);
            }
        })
    }

    loadServicii() {
        this.isLoading = true;
        this.serviciiService.getServicii().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.servicii = res;
            },
            error: (err) => {
                console.error('Error in get servicii: ', err);
            }
        })
    }

    getNumeZi(dayOfWeek: number): string {
        const zi = this.zileSaptamana.find(z => z.value === dayOfWeek);
        return zi ? zi.key : 'N/A';
    }

    get zileSaptamanaDisponibile(): { key: string; value: number }[] {
        const zileExistente = this.zile.map(z => z.day_of_week);
        return this.zileSaptamana.filter(z => !zileExistente.includes(z.value));
    }

    removeZi(index: number) {
        this.zile.splice(index, 1);
    }

    addZi() {
        if (this.newZi.day_of_week === -1) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați o zi din săptămână.',
                this.errorIcon
            );
            return;
        }
        if (!this.newZi.time_from) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați ora.',
                this.errorIcon
            );
            return;
        }

        // Verifică dacă ziua există deja
        if (this.zile.some(z => z.day_of_week === this.newZi.day_of_week)) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Această zi este deja adăugată.',
                this.errorIcon
            );
            return;
        }

        this.zile.push({
            day_of_week: this.newZi.day_of_week,
            time_from: this.newZi.time_from
        });

        // Sortează zilele după day_of_week
        this.zile.sort((a, b) => {
            const aVal = a.day_of_week === 0 ? 7 : a.day_of_week;
            const bVal = b.day_of_week === 0 ? 7 : b.day_of_week;
            return aVal - bVal;
        });

        // Reset form
        this.newZi = { day_of_week: -1, time_from: '' };
        this.showAddZi = false;
    }

    cancelAddZi() {
        this.newZi = { day_of_week: -1, time_from: '' };
        this.showAddZi = false;
    }

    submitForm() {
        // Validări
        const selectedModul = this.modulControl.value;
        const selectedLocatie = this.locatieControl.value;
        const selectedServiciu = this.serviciuControl.value;

        if (!selectedModul?.id) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați un modul valid.',
                this.errorIcon
            );
            return;
        }

        if (!selectedLocatie?.id) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați o locație validă.',
                this.errorIcon
            );
            return;
        }

        if (!selectedServiciu?.id) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Selectați un serviciu valid.',
                this.errorIcon
            );
            return;
        }

        if (this.zile.length === 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                'Adăugați cel puțin o zi.',
                this.errorIcon
            );
            return;
        }

        this.isLoading = true;

        const createData: SlotGrupUpdate = {
            id_modul: selectedModul.id,
            id_locatie: selectedLocatie.id,
            id_serviciu: selectedServiciu.id,
            emails: this.emails || '',
            zile: this.zile.map(z => ({
                day_of_week: z.day_of_week,
                time_from: z.time_from
            }))
        };

        this.programariSloturiUltimaOraService
            .updateGroup(createData)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
